import React, { useCallback, useEffect, useState } from "react";
import { productPickerComposer } from "ReactReducers/ProductPicker/product-picker-composer";
import { profileComposer } from "ReactReducers/Profile/profile-composer";
import ViewMoreSection from "ReactComponents/Shared/AnimationComponents/view-more-section";
import ProductPicker from "ReactComponents/ProductPage/ProductPicker/product-picker";
// import InspectionCopyLink from "ReactComponents/InspectionCopy/inspection-copy";
import IconLogo from "ReactComponents/Shared/Icons/icon-logo";
import ProductDescriptionErrorBoundary from "./ProductDescription/product-description-error-boundary";
import ProductDescription from "./ProductDescription/product-description";
import * as tracking from "ReactComponents/Shared/Utils/tracking";
import IWork from "interfaces/IWork";
import ILabels from "interfaces/ILabels";
import IBasketItem from "interfaces/IBasketItem";
import ITrialLicense from "interfaces/ITrialLicense";
import TrialLicenseLink from "ReactComponents/TrialLicense/trial-license-link";
import { IProfileState } from "ReactReducers/Profile/profile-reducer";
import { Draft } from "@reduxjs/toolkit";
import IInspectionCopy from "interfaces/IInspectionCopy";
import ProductMediaLink from "./ProductMediaLink/product-media-link";
import IconBars from "ReactComponents/Shared/Icons/icon-bars";
import IconBookOpen from "ReactComponents/Shared/Icons/icon-book-open";
import ProductReviewBlock from "./product-review-block";
import Facts from "./tab-facts";
import FreeMaterials from "ReactComponents/ProductPage/tab-free-materials";
import IconWebsite from "ReactComponents/Shared/Icons/icon-website";
import Tabs from "ReactComponents/Tabs/tabs";
import Tab from "ReactComponents/Tabs/tab";
import ProductSlider from "ReactComponents/ProductSlider/product-slider";
import { ADD_TO_BASKET_LOCALSTORAGE_KEY } from "../../constants";
import { FormColumnProps } from "ReactComponents/TwoColumnGrid/two-column-grid";
import DynamicContactForm from "ReactComponents/DynamicContactForm/dynamic-contact-form";

export interface IProductPageProps {
    SiteName: string;
    ProductIndex: number;
    Work: IWork;
    Labels: ILabels;
    productPickerWork?: IWork;
    productPickerProduct?: number;
    amount: number;
    TrialLicense?: ITrialLicense;
    ShowAddTodoButton: boolean;
    ShowReviewLink: boolean;
    loggedIn: boolean;
    isAdding: string[];
    DynamicContactFormViewModel?: FormColumnProps;
    userLogin: (state: Draft<IProfileState>) => Draft<IProfileState>;
    InspectionCopy?: IInspectionCopy;
    setProductIndex: (productIndex: number) => void;
    basketAdd: (basketItem: IBasketItem) => void;
    setAmount: (quantity: number) => void;
    setWork: (work: IWork) => void;
}

const ProductPage: React.FC<IProductPageProps> = props => {
    console.log("ProductPage", props);
    const [currentProductIndex, setCurrentProductIndex] = useState(0);

    const { ...formProps } = props.DynamicContactFormViewModel ?? null;

    const onChangeProduct = useCallback(
        (e: { target: { value: number } }) => {
            const productChanged = e.target.value !== currentProductIndex;
            if (productChanged) {
                const work = props.productPickerWork ? props.productPickerWork : props.Work;
                const product = work.Products[e.target.value];

                if (product.ProductPageLink) {
                    const url = window.location.origin + product.ProductPageLink;
                    window.history.replaceState({}, "", url);
                }

                setCurrentProductIndex(e.target.value);
                props.setProductIndex(e.target.value);

                tracking.productDetailsView({ product });
            }
        },
        [
            props.productPickerWork,
            props.Work,
            currentProductIndex,
            setCurrentProductIndex,
            props.setProductIndex
        ]
    );

    const addToBasket = useCallback(() => {
        const work = props.productPickerWork ? props.productPickerWork : props.Work;
        const index = props.productPickerProduct ?? currentProductIndex;
        const product = work.Products[index];
        const addToBasketItem = localStorage.getItem(ADD_TO_BASKET_LOCALSTORAGE_KEY);

        const basketItem = {
            Id: product.Id,
            amount: props.amount,
            product: product
        };

        if (addToBasketItem) {
            const parsedObj = JSON.parse(addToBasketItem);
            basketItem.Id = parsedObj.product.Id;
            basketItem.amount = parsedObj.product.Amount;
            basketItem.product = parsedObj.product;
        }

        props.basketAdd(basketItem);
    }, [
        props.productPickerWork,
        props.Work,
        props.basketAdd,
        currentProductIndex,
        props.productPickerProduct,
        props.amount
    ]);

    useEffect(() => {
        setCurrentProductIndex(props.ProductIndex || 0);
        props.setProductIndex(props.ProductIndex ?? 0);

        tracking.productDetailsView({ product: props.Work.Products[props.ProductIndex] });
        props.setWork(props.Work);
    }, []);

    /* Automatically add to basket if "addToBasketOnLoginReturn" key is set in local storage */
    useEffect(() => {
        const basketItemFromLocalStorage = localStorage.getItem(ADD_TO_BASKET_LOCALSTORAGE_KEY);

        if (!props.loggedIn) {
            localStorage.removeItem(ADD_TO_BASKET_LOCALSTORAGE_KEY);
        }

        if (basketItemFromLocalStorage && props.loggedIn) {
            addToBasket();

            localStorage.removeItem(ADD_TO_BASKET_LOCALSTORAGE_KEY);
        }
    }, []);

    const work = props.productPickerWork ?? props.Work;
    const index = props.productPickerProduct ?? currentProductIndex;
    const product = work.Products[index];

    const licenceProduct = props.TrialLicense && work.Products[props.TrialLicense.ProductIndex];
    // Insert potential word break after . to gracefully break website titles
    const formattedTitle = product.Title.replace(/\./g, ".<wbr>");

    const trialLicenseLinkProps = {
        basketAdd: props.basketAdd,
        TrialLicense: props.TrialLicense,
        product: licenceProduct,
        siteName: props.SiteName,
        isAdding: props.isAdding
    };

    return (
        <>
            <div className="product-page-container">
                <div className="product-details-container">
                    <div className="product-left-container">
                        <div className="product__img">
                            {product.CoverImageUrl ? (
                                <img
                                    src={product.CoverImageUrl + "&w=320"}
                                    loading="lazy"
                                    alt={`Forsideillustration til ${product.Title}`}
                                />
                            ) : (
                                <div className="product__img--fallback">
                                    <IconLogo />
                                </div>
                            )}
                        </div>
                        <div className="product__media">
                            {product.CustomLink &&
                                product.CustomLink.LinkText &&
                                product.CustomLink.LinkUrl && (
                                    <ProductMediaLink
                                        label={product.CustomLink.LinkText}
                                        url={product.CustomLink.LinkUrl}
                                        target={product.CustomLink.Target}
                                        icon={<IconBookOpen />}
                                    />
                                )}
                            {props.Labels.ReadingSampleLink && (
                                <ProductMediaLink
                                    label={props.Labels.ReadingSampleLink}
                                    url=""
                                    icon={<IconBookOpen />}
                                />
                            )}
                            {product.HasTrialAccess && props.TrialLicense && (
                                <TrialLicenseLink {...trialLicenseLinkProps} />
                            )}
                            {product.SampleUrl && (
                                <ProductMediaLink
                                    label={product.SampleUrl.Text}
                                    url={product.SampleUrl.Url}
                                    icon={<IconBars />}
                                />
                            )}
                            {product.DigitalProductUrl && (
                                <ProductMediaLink
                                    label={product.DigitalProductUrl.Text}
                                    url={product.DigitalProductUrl.Url}
                                    icon={<IconWebsite />}
                                />
                            )}
                            {props.InspectionCopy &&
                                props.InspectionCopy.ShowInspectionCopyLink &&
                                props.DynamicContactFormViewModel &&
                                props.DynamicContactFormViewModel?.DynamicFieldsList.length > 0 && (
                                    <ProductMediaLink
                                        icon={<IconBookOpen />}
                                        label={props.InspectionCopy.ButtonText}
                                        target=""
                                        url="#DynamicContactFormAnchorLink"
                                    />
                                )}
                        </div>
                    </div>

                    <div className="product__meta">
                        <div className="product__meta__info">
                            <span className="product__category">{props.Work.Category}</span>
                            <h1
                                className="product__title u-text-word-break-word"
                                dangerouslySetInnerHTML={{ __html: formattedTitle }}
                            />

                            {product.Subtitle && <p className="product__subtitle">{product.Subtitle}</p>}

                            {product.EditorsListLong && (
                                <div className="product__editors">
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: product.EditorsListLong
                                        }}
                                    />

                                    {props.Labels.EditorsListShortAbbreviation && (
                                        <span
                                            className="editor-abbreviation"
                                            dangerouslySetInnerHTML={{
                                                __html: props.Labels.EditorsListShortAbbreviation
                                            }}
                                        />
                                    )}
                                </div>
                            )}

                            {!product.EditorsListLong && product.AuthorsListLong && (
                                <div className="product__authors">
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: product.AuthorsListLong
                                        }}
                                    />
                                </div>
                            )}

                            {product.SeriesLinks &&
                                product.SeriesLinks.length > 0 &&
                                product.SeriesLinks.map((link, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className="product__series-link"
                                            dangerouslySetInnerHTML={{ __html: link }}
                                        />
                                    );
                                })}
                        </div>

                        <Tabs>
                            {!!product.Description && (
                                <Tab label={props.Labels.ProductDescriptionText}>
                                    <ViewMoreSection>
                                        <ProductDescriptionErrorBoundary description={product.Description}>
                                            <ProductDescription description={product.Description} />
                                        </ProductDescriptionErrorBoundary>
                                    </ViewMoreSection>
                                </Tab>
                            )}

                            {product.Reviews.length > 0 && (
                                <Tab label={props.Labels.ProductReviewsTitleText}>
                                    <ProductReviewBlock
                                        productReviews={product.Reviews}
                                        productReviewsTitleText={props.Labels.ProductReviewsTitleText}
                                    />
                                </Tab>
                            )}

                            {!!product.FreeMaterials && (
                                <Tab label={props.Labels.ExtraMaterialsTab}>
                                    <FreeMaterials freeMaterials={product.FreeMaterials} />
                                </Tab>
                            )}

                            {product.ShowFacts && (
                                <Tab label={props.Labels.FactsTab}>
                                    <Facts product={product} labels={props.Labels} />
                                </Tab>
                            )}
                        </Tabs>

                        {product.SystemLinks &&
                            product.SystemLinks.length > 0 &&
                            product.SystemLinks.map((link, index) => {
                                return (
                                    <div
                                        key={index}
                                        className="product__product-context"
                                        dangerouslySetInnerHTML={{ __html: link }}
                                    />
                                );
                            })}
                    </div>

                    <div className="buy-container">
                        <ProductPicker
                            work={work}
                            showAddTodoButton={props.ShowAddTodoButton}
                            currentProductIndex={currentProductIndex}
                            currentProduct={product}
                            onChange={onChangeProduct}
                            addToBasket={addToBasket}
                            {...props}
                        />
                    </div>
                </div>

                {props.InspectionCopy &&
                    props.InspectionCopy.ShowInspectionCopyLink &&
                    props.DynamicContactFormViewModel &&
                    props.DynamicContactFormViewModel?.DynamicFieldsList.length > 0 && (
                        <div id="DynamicContactFormAnchorLink">
                            <DynamicContactForm {...formProps} />
                        </div>
                    )}

                {(product.SameSeriesProducts || product.SameSubjectProducts) && (
                    <div className="product-page__sliders">
                        {product.SameSeriesProducts && (
                            <ProductSlider
                                Works={product.SameSeriesProducts.RelatedProducts}
                                HideWhenEmpty={true}
                                TitleText={product.SameSeriesProducts.TabTitle}
                                ViewAllLink={product.SameSeriesProducts.ViewAllLink}
                                ViewAllLinkText={product.SameSeriesProducts.ViewAllLinkText}
                                Labels={props.Labels}
                                EmptySliderText="Ingen produkter"
                                ProductDeliveryInfo={props.Work.ProductDeliveryInfo}
                            />
                        )}

                        {product.SameSubjectProducts && (
                            <ProductSlider
                                Works={product.SameSubjectProducts.RelatedProducts}
                                HideWhenEmpty={true}
                                TitleText={product.SameSubjectProducts.TabTitle}
                                ViewAllLink={product.SameSubjectProducts.ViewAllLink}
                                ViewAllLinkText={product.SameSubjectProducts.ViewAllLinkText}
                                Labels={props.Labels}
                                EmptySliderText="Ingen produkter"
                                ProductDeliveryInfo={props.Work.ProductDeliveryInfo}
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default productPickerComposer(profileComposer(ProductPage));
